// Зарплата по часам. Ставки/правила/праздники/премии — решение владельца/
// админа (HR), бэкенд их и ограничивает @Roles('owner','admin'); расчёт и
// закрытие периода — инструмент бухгалтера, доступен и ей. Часы считаются из
// длительности ЗАКРЫТЫХ смен (сотрудник = кто открыл смену) — открытая смена
// попадёт в расчёт того периода, когда закроется. См. payroll-module-design.md.

import { useState } from 'react'
import type {
  PayrollAdjustment,
  PayrollDayRules,
  PayrollHoliday,
  PayrollPreview,
  PayrollRate,
  PayrollRunDetail,
  PayrollRunSummary
} from '@billiard/shared'
import { api } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { useFetch, useMutate } from '@/hooks/useApi'
import { type PinUser } from '@/lib/config'
import {
  AsyncState,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Kpi,
  Modal,
  PageHead,
  Select,
  SectionHead
} from '@/components/ui'
import { count, dateOnly, dateInputToMs, dateTime, money, msToDateInput } from '@/lib/format'
import { Cur } from '@/lib/currency'

const DAY_MS = 24 * 60 * 60 * 1000
type Tab = 'rates' | 'rules' | 'adjustments' | 'calc'

export function Payroll(): React.JSX.Element {
  const { hasRole, isSuperAdmin } = useAuth()
  const canManage = isSuperAdmin || hasRole('owner', 'admin')
  const [tab, setTab] = useState<Tab>('calc')

  const users = useFetch<PinUser[]>('/v1/users')
  const operators = users.data ?? []

  return (
    <>
      <PageHead
        eyebrow="Финансы"
        title="Зарплата"
        lead="Расчёт по часам из длительности смен: ставка сотрудника, коэффициенты за выходные и
          праздники, разовые премии и удержания."
      />

      <div className="tabs" style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
        <TabButton active={tab === 'calc'} onClick={() => setTab('calc')}>
          Расчёт
        </TabButton>
        <TabButton active={tab === 'rates'} onClick={() => setTab('rates')}>
          Ставки
        </TabButton>
        <TabButton active={tab === 'rules'} onClick={() => setTab('rules')}>
          Правила и праздники
        </TabButton>
        <TabButton active={tab === 'adjustments'} onClick={() => setTab('adjustments')}>
          Премии
        </TabButton>
      </div>

      {tab === 'calc' && <CalcTab />}
      {tab === 'rates' && <RatesTab operators={operators} canManage={canManage} />}
      {tab === 'rules' && <RulesTab canManage={canManage} />}
      {tab === 'adjustments' && <AdjustmentsTab operators={operators} canManage={canManage} />}
    </>
  )
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <button className={active ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'} onClick={onClick}>
      {children}
    </button>
  )
}

function userName(operators: PinUser[], userId: string): string {
  return operators.find((u) => u.id === userId)?.name ?? userId.slice(0, 8)
}

// ═══════════════════════════ Ставки ═══════════════════════════

function RatesTab({ operators, canManage }: { operators: PinUser[]; canManage: boolean }): React.JSX.Element {
  const [userId, setUserId] = useState('')
  const rates = useFetch<PayrollRate[]>(userId ? '/v1/payroll/rates' : null, { userId }, [userId])
  const save = useMutate()
  const [hourlyRate, setHourlyRate] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState(() => msToDateInput(Date.now()))

  const onAdd = async (): Promise<void> => {
    const rate = Number(hourlyRate)
    if (!userId || !Number.isFinite(rate) || rate <= 0) return
    const ok = await save.mutate(() =>
      api.post('/v1/payroll/rates', { userId, hourlyRate: rate, effectiveFrom: dateInputToMs(effectiveFrom) })
    )
    if (ok) {
      setHourlyRate('')
      rates.reload()
    }
  }

  return (
    <>
      <SectionHead title="Ставка сотрудника" meta="История — новая запись, а не правка старой" />
      <div className="form-grid">
        <Field label="Сотрудник" htmlFor="rt-user">
          <Select id="rt-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">— выберите —</option>
            {operators.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {!userId && <EmptyState message="Выберите сотрудника, чтобы увидеть и завести ставку." />}

      {userId && (
        <>
          {canManage && (
            <div className="toolbar toolbar--compact">
              <Input
                type="number"
                min={0}
                step="0.01"
                placeholder="Ставка, руб/час"
                style={{ maxWidth: 160 }}
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
              />
              <Input
                type="date"
                style={{ maxWidth: 170 }}
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
              <Button variant="primary" size="sm" busy={save.saving} onClick={() => void onAdd()}>
                Добавить ставку
              </Button>
            </div>
          )}
          {save.error && <ErrorState message={save.error} />}

          <AsyncState query={rates} loadingLabel="Загружаем историю ставок…" />
          {rates.data && rates.data.length === 0 && (
            <EmptyState message="Ставка не задана — часы этого сотрудника не посчитаются в расчёте." />
          )}
          {rates.data && rates.data.length > 0 && (
            <div className="table-wrap">
              <table className="rtable">
                <thead>
                  <tr>
                    <th>Ставка</th>
                    <th>Действует с</th>
                    <th>Действует по</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.data.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {money(r.hourlyRate)} <Cur />
                        /ч
                      </td>
                      <td>{dateOnly(r.effectiveFrom)}</td>
                      <td>{r.effectiveTo ? dateOnly(r.effectiveTo) : 'по сей день'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  )
}

// ═══════════════════════════ Правила и праздники ═══════════════════════════

function RulesTab({ canManage }: { canManage: boolean }): React.JSX.Element {
  const rules = useFetch<PayrollDayRules>('/v1/payroll/rules')
  const saveRules = useMutate()
  const [weekday, setWeekday] = useState('1')
  const [weekend, setWeekend] = useState('1')
  const [hydrated, setHydrated] = useState(false)
  if (rules.data && !hydrated) {
    setWeekday(String(rules.data.weekdayMultiplier))
    setWeekend(String(rules.data.weekendMultiplier))
    setHydrated(true)
  }

  const onSaveRules = async (): Promise<void> => {
    const ok = await saveRules.mutate(() =>
      api.put('/v1/payroll/rules', {
        weekdayMultiplier: Number(weekday),
        weekendMultiplier: Number(weekend)
      })
    )
    if (ok) rules.reload()
  }

  const year = new Date().getFullYear()
  const holidays = useFetch<PayrollHoliday[]>('/v1/payroll/holidays', { year }, [year])
  const saveHoliday = useMutate()
  const delHoliday = useMutate()
  const [hDate, setHDate] = useState('')
  const [hName, setHName] = useState('')
  const [hMult, setHMult] = useState('2')

  const onAddHoliday = async (): Promise<void> => {
    if (!hDate || !hName.trim()) return
    const ok = await saveHoliday.mutate(() =>
      api.post('/v1/payroll/holidays', { date: hDate, name: hName.trim(), multiplier: Number(hMult) })
    )
    if (ok) {
      setHDate('')
      setHName('')
      holidays.reload()
    }
  }

  const onDeleteHoliday = async (id: string): Promise<void> => {
    const ok = await delHoliday.mutate(() => api.del(`/v1/payroll/holidays/${id}`))
    if (ok) holidays.reload()
  }

  return (
    <>
      <SectionHead title="Множители будни/выходные" meta="Применяются, если день не отмечен как праздник" />
      <div className="toolbar toolbar--compact">
        <Field label="Будни">
          <Input
            type="number"
            min={0}
            step="0.1"
            style={{ maxWidth: 100 }}
            value={weekday}
            disabled={!canManage}
            onChange={(e) => setWeekday(e.target.value)}
          />
        </Field>
        <Field label="Выходные (сб/вс)">
          <Input
            type="number"
            min={0}
            step="0.1"
            style={{ maxWidth: 100 }}
            value={weekend}
            disabled={!canManage}
            onChange={(e) => setWeekend(e.target.value)}
          />
        </Field>
        {canManage && (
          <Button
            variant="primary"
            size="sm"
            busy={saveRules.saving}
            onClick={() => void onSaveRules()}
            style={{ alignSelf: 'flex-end' }}
          >
            Сохранить
          </Button>
        )}
      </div>
      {saveRules.error && <ErrorState message={saveRules.error} />}

      <SectionHead title={`Праздники ${year}`} meta="Перекрывают правило будни/выходные — даже если праздник в будний день" />
      {canManage && (
        <div className="toolbar toolbar--compact">
          <Input type="date" style={{ maxWidth: 170 }} value={hDate} onChange={(e) => setHDate(e.target.value)} />
          <Input
            placeholder="Название"
            style={{ maxWidth: 200 }}
            value={hName}
            onChange={(e) => setHName(e.target.value)}
          />
          <Input
            type="number"
            min={0}
            step="0.1"
            placeholder="Коэфф."
            style={{ maxWidth: 100 }}
            value={hMult}
            onChange={(e) => setHMult(e.target.value)}
          />
          <Button variant="primary" size="sm" busy={saveHoliday.saving} onClick={() => void onAddHoliday()}>
            Добавить
          </Button>
        </div>
      )}
      {saveHoliday.error && <ErrorState message={saveHoliday.error} />}
      {delHoliday.error && <ErrorState message={delHoliday.error} />}

      <AsyncState query={holidays} loadingLabel="Загружаем праздники…" />
      {holidays.data && holidays.data.length === 0 && (
        <EmptyState message="Праздники не заведены — в расчёте будут только будни/выходные." />
      )}
      {holidays.data && holidays.data.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Название</th>
                <th>Коэффициент</th>
                {canManage && <th style={{ width: 80 }} />}
              </tr>
            </thead>
            <tbody>
              {holidays.data.map((h) => (
                <tr key={h.id}>
                  <td>{h.date}</td>
                  <td>{h.name}</td>
                  <td>×{h.multiplier}</td>
                  {canManage && (
                    <td>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={delHoliday.saving}
                        onClick={() => void onDeleteHoliday(h.id)}
                      >
                        Удалить
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════ Премии/удержания ═══════════════════════════

function AdjustmentsTab({
  operators,
  canManage
}: {
  operators: PinUser[]
  canManage: boolean
}): React.JSX.Element {
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const list = useFetch<PayrollAdjustment[]>('/v1/payroll/adjustments', { from: fromStr, to: toStr }, [
    fromStr,
    toStr
  ])
  const save = useMutate()
  const del = useMutate()

  const [userId, setUserId] = useState('')
  const [date, setDate] = useState(() => msToDateInput(now))
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  const onAdd = async (): Promise<void> => {
    const amt = Number(amount)
    if (!userId || !Number.isFinite(amt) || amt === 0 || !reason.trim()) return
    const ok = await save.mutate(() =>
      api.post('/v1/payroll/adjustments', { userId, date, amount: amt, reason: reason.trim() })
    )
    if (ok) {
      setAmount('')
      setReason('')
      list.reload()
    }
  }

  const onDelete = async (id: string): Promise<void> => {
    const ok = await del.mutate(() => api.del(`/v1/payroll/adjustments/${id}`))
    if (ok) list.reload()
  }

  return (
    <>
      <SectionHead title="Премии и удержания" meta="Сумма может быть отрицательной — это удержание" />
      <div className="filters" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '16px 0' }}>
        <Field label="С" htmlFor="pa-from">
          <Input id="pa-from" type="date" value={fromStr} onChange={(e) => setFromStr(e.target.value)} />
        </Field>
        <Field label="По" htmlFor="pa-to">
          <Input id="pa-to" type="date" value={toStr} onChange={(e) => setToStr(e.target.value)} />
        </Field>
      </div>

      {canManage && (
        <div className="toolbar toolbar--compact">
          <Select value={userId} onChange={(e) => setUserId(e.target.value)} style={{ maxWidth: 180 }}>
            <option value="">Сотрудник…</option>
            {operators.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
          <Input type="date" style={{ maxWidth: 170 }} value={date} onChange={(e) => setDate(e.target.value)} />
          <Input
            type="number"
            step="0.01"
            placeholder="Сумма (− для удержания)"
            style={{ maxWidth: 180 }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Input
            placeholder="Причина"
            style={{ maxWidth: 220 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button variant="primary" size="sm" busy={save.saving} onClick={() => void onAdd()}>
            Добавить
          </Button>
        </div>
      )}
      {save.error && <ErrorState message={save.error} />}
      {del.error && <ErrorState message={del.error} />}

      <AsyncState query={list} loadingLabel="Загружаем начисления…" />
      {list.data && list.data.length === 0 && <EmptyState message="За период начислений нет." />}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Сотрудник</th>
                <th className="num">Сумма</th>
                <th>Причина</th>
                {canManage && <th style={{ width: 80 }} />}
              </tr>
            </thead>
            <tbody>
              {list.data.map((a) => (
                <tr key={a.id}>
                  <td>{a.date}</td>
                  <td>{userName(operators, a.userId)}</td>
                  <td className="num">
                    {money(a.amount)} <Cur />
                  </td>
                  <td>{a.reason}</td>
                  {canManage && (
                    <td>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={del.saving}
                        onClick={() => void onDelete(a.id)}
                      >
                        Удалить
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════ Расчёт ═══════════════════════════

function CalcTab(): React.JSX.Element {
  const { hasRole, isSuperAdmin } = useAuth()
  const canClose = isSuperAdmin || hasRole('owner', 'admin', 'accountant')
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 14 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const calc = useMutate()
  const close = useMutate()
  const [preview, setPreview] = useState<PayrollPreview | null>(null)

  const from = dateInputToMs(fromStr)
  const to = dateInputToMs(toStr) + DAY_MS - 1

  const runs = useFetch<PayrollRunSummary[]>('/v1/payroll/runs')
  const [openRun, setOpenRun] = useState<PayrollRunDetail | null>(null)
  const runDetail = useMutate()

  const onCalc = async (): Promise<void> => {
    setPreview(null)
    await calc.mutate(async () => {
      const res = await api.post<PayrollPreview>('/v1/payroll/preview', { from, to })
      setPreview(res)
    })
  }

  const onClose = async (): Promise<void> => {
    if (!preview) return
    const ok = await close.mutate(() => api.post('/v1/payroll/runs', { from, to }))
    if (ok) {
      setPreview(null)
      runs.reload()
    }
  }

  const onOpenRun = async (id: string): Promise<void> => {
    await runDetail.mutate(async () => {
      const res = await api.get<PayrollRunDetail>(`/v1/payroll/runs/${id}`)
      setOpenRun(res)
    })
  }

  const onMarkPaid = async (id: string): Promise<void> => {
    const ok = await runDetail.mutate(() => api.patch(`/v1/payroll/runs/${id}/paid`))
    if (ok) {
      runs.reload()
      if (openRun?.id === id) onOpenRun(id)
    }
  }

  return (
    <>
      <SectionHead title="Расчёт периода" />
      <div className="filters" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '16px 0' }}>
        <Field label="С" htmlFor="cl-from">
          <Input id="cl-from" type="date" value={fromStr} onChange={(e) => setFromStr(e.target.value)} />
        </Field>
        <Field label="По" htmlFor="cl-to">
          <Input id="cl-to" type="date" value={toStr} onChange={(e) => setToStr(e.target.value)} />
        </Field>
        <Button variant="primary" size="sm" busy={calc.saving} onClick={() => void onCalc()}>
          Посчитать
        </Button>
      </div>
      {calc.error && <ErrorState message={calc.error} />}

      {preview && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 4 }}>
            <Kpi label="Сотрудников" value={count(preview.lines.length)} />
            <Kpi label="Итого начислено" value={<>{money(preview.totalAmount)} <Cur /></>} gold accent />
            {preview.hasIssues && (
              <Kpi label="Проблемных строк" value={count(preview.lines.filter((l) => l.hasIssue).length)} sub="Нет ставки на дату работы" />
            )}
          </div>

          {preview.lines.length === 0 ? (
            <EmptyState message="За период нет отработанных часов и начислений." />
          ) : (
            <div className="table-wrap">
              <table className="rtable">
                <thead>
                  <tr>
                    <th>Сотрудник</th>
                    <th className="num">Часы</th>
                    <th className="num">База</th>
                    <th className="num">Премии/удержания</th>
                    <th className="num">Итого</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((l) => (
                    <tr key={l.userId}>
                      <td>{l.userName}</td>
                      <td className="num">{l.hoursWorked}</td>
                      <td className="num">
                        {money(l.baseAmount)} <Cur />
                      </td>
                      <td className="num">
                        {money(l.adjustmentsAmount)} <Cur />
                      </td>
                      <td className="num">
                        {money(l.totalAmount)} <Cur />
                      </td>
                      <td>
                        {l.hasIssue && (
                          <span
                            className="pill"
                            style={{
                              background: 'rgba(239,68,68,0.12)',
                              color: 'var(--red)',
                              border: '1px solid rgba(239,68,68,0.3)'
                            }}
                          >
                            Нет ставки
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="toolbar toolbar--compact">
            <Button
              variant="primary"
              busy={close.saving}
              disabled={!canClose || preview.hasIssues || preview.lines.length === 0}
              onClick={() => void onClose()}
            >
              Закрыть период
            </Button>
          </div>
          {close.error && <ErrorState message={close.error} />}
        </>
      )}

      <SectionHead title="Закрытые периоды" />
      <AsyncState query={runs} loadingLabel="Загружаем историю…" />
      {runs.data && runs.data.length === 0 && <EmptyState message="Закрытых периодов ещё нет." />}
      {runs.data && runs.data.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Период</th>
                <th className="num">Сумма</th>
                <th>Статус</th>
                <th>Закрыт</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id}>
                  <td>
                    {dateOnly(r.periodFrom)} — {dateOnly(r.periodTo)}
                  </td>
                  <td className="num">
                    {money(r.totalAmount)} <Cur />
                  </td>
                  <td>
                    {r.status === 'paid' ? (
                      <span className="pill pill--open">Выплачен</span>
                    ) : (
                      <span className="pill pill--closed">Закрыт</span>
                    )}
                  </td>
                  <td>{dateTime(r.closedAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn--ghost btn--sm" onClick={() => void onOpenRun(r.id)}>
                        Открыть
                      </button>
                      {r.status !== 'paid' && canClose && (
                        <button className="btn btn--ghost btn--sm" onClick={() => void onMarkPaid(r.id)}>
                          Отметить оплаченным
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openRun && (
        <Modal title={`Период ${dateOnly(openRun.periodFrom)} — ${dateOnly(openRun.periodTo)}`} onClose={() => setOpenRun(null)} wide>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th className="num">Часы</th>
                  <th className="num">База</th>
                  <th className="num">Премии/удержания</th>
                  <th className="num">Итого</th>
                </tr>
              </thead>
              <tbody>
                {openRun.lines.map((l) => (
                  <tr key={l.userId}>
                    <td>{l.userName}</td>
                    <td className="num">{l.hoursWorked}</td>
                    <td className="num">
                      {money(l.baseAmount)} <Cur />
                    </td>
                    <td className="num">
                      {money(l.adjustmentsAmount)} <Cur />
                    </td>
                    <td className="num">
                      {money(l.totalAmount)} <Cur />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </>
  )
}
